import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';
import { useNavigate } from 'react-router-dom';

export default function StructuralViolencePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-rose-50 to-purple-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <span className="bg-rose-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
              Structural Violence
            </span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            The Invisible Violence: When Safety Becomes Performance
          </h1>
          <p className="text-xl text-gray-700 mb-8 leading-relaxed">
            Is a woman's role to pretend she's on the phone while walking home alone? To act strong after psychological violence? To hide trauma from her children by pretending to be happy?
          </p>
          <div className="flex items-center gap-4 text-gray-600">
            <span className="font-semibold">SheCan Team</span>
            <span>•</span>
            <span>January 30, 2025</span>
            <span>•</span>
            <span>15 min read</span>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* Introduction */}
        <div className="prose prose-lg max-w-none mb-12">
          <p className="text-xl text-gray-800 leading-relaxed mb-8">
            Every evening, millions of women around the world perform the same ritual: they pull out their phones, pretend to be in conversation, and walk home with fake confidence. In Turkey, they say <strong>"yerinde psikolojik şiddet görüp güçlüymüş gibi yapmak"</strong> — acting strong after experiencing psychological violence. In France, it's <strong>"faire semblant de parler au téléphone en rentrant à la maison seule"</strong> — pretending to talk on the phone while coming home alone.
          </p>
          
          <p className="text-xl text-gray-800 leading-relaxed mb-8">
            This is <strong>structural violence</strong> — the invisible, normalized violence that society accepts as "just how things are." It's not a single act of aggression. It's the daily performance of safety that women must enact simply to exist in public spaces.
          </p>
        </div>

        {/* Image */}
        <div className="mb-12 rounded-2xl overflow-hidden">
          <img 
            src="https://public.readdy.ai/ai/img_res/cb7802487b8ac896d76332c3863f011a.jpg"
            alt="Woman pretending to be on phone while walking alone"
            className="w-full h-96 object-cover object-top"
          />
        </div>

        {/* Section 1: The Performance of Safety */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">The Performance of Safety</h2>
          
          <div className="bg-rose-50 border-l-4 border-rose-600 p-6 mb-8 rounded-r-lg">
            <p className="text-lg text-gray-800 italic">
              "I hold my keys between my fingers like a weapon. I cross the street when I see a group of men. I pretend to be on the phone with my boyfriend even though I'm single. I've been doing this since I was 14. This is just... normal."
            </p>
            <p className="text-sm text-gray-600 mt-3">— Sarah, 28, Luxembourg</p>
          </div>

          <p className="text-lg text-gray-800 leading-relaxed mb-6">
            Women learn these survival strategies early. By adolescence, most girls have already internalized the rules:
          </p>

          <ul className="space-y-4 mb-8">
            <li className="flex items-start gap-3">
              <i className="ri-alert-line text-2xl text-rose-600 mt-1"></i>
              <div>
                <strong className="text-gray-900">Don't walk alone at night</strong>
                <p className="text-gray-700">But what if you work late? What if you don't have a car? What if public transport stops running?</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <i className="ri-alert-line text-2xl text-rose-600 mt-1"></i>
              <div>
                <strong className="text-gray-900">Don't dress provocatively</strong>
                <p className="text-gray-700">But who decides what's provocative? And why is the solution to restrict women's clothing instead of men's behavior?</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <i className="ri-alert-line text-2xl text-rose-600 mt-1"></i>
              <div>
                <strong className="text-gray-900">Always be aware of your surroundings</strong>
                <p className="text-gray-700">Constant hypervigilance is exhausting. It's a form of chronic stress that affects mental and physical health.</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <i className="ri-alert-line text-2xl text-rose-600 mt-1"></i>
              <div>
                <strong className="text-gray-900">Pretend to be on the phone</strong>
                <p className="text-gray-700">Because appearing connected to someone else makes you less of a target. Your safety depends on performing unavailability.</p>
              </div>
            </li>
          </ul>

          <p className="text-lg text-gray-800 leading-relaxed">
            These aren't safety tips. They're symptoms of a society that has normalized women's fear and placed the burden of prevention on victims rather than perpetrators.
          </p>
        </div>

        {/* Section 2: Psychological Violence and the Mask of Strength */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Acting Strong After Psychological Violence</h2>
          
          <p className="text-lg text-gray-800 leading-relaxed mb-6">
            In Turkey, there's a phrase that captures another dimension of structural violence: <strong>"yerinde psikolojik şiddet görüp güçlüymüş gibi yapmak"</strong> — experiencing psychological violence in the moment and acting as if you're strong.
          </p>

          <div className="bg-purple-50 border-l-4 border-purple-600 p-6 mb-8 rounded-r-lg">
            <p className="text-lg text-gray-800 italic">
              "My husband criticizes everything I do. He tells me I'm stupid, that I can't do anything right. But when we're with friends, I smile and laugh. I act like everything is fine. Because if I show weakness, it gets worse. And if I tell anyone, they'll say I'm exaggerating or that I should just leave — as if it's that simple."
            </p>
            <p className="text-sm text-gray-600 mt-3">— Ayşe, 35, Istanbul</p>
          </div>

          <p className="text-lg text-gray-800 leading-relaxed mb-6">
            Psychological violence is insidious because it leaves no visible scars. It includes:
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-chat-delete-line text-rose-600 text-xl"></i>
                Verbal Abuse
              </h3>
              <p className="text-gray-700">Constant criticism, insults, humiliation, name-calling, gaslighting</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-eye-line text-rose-600 text-xl"></i>
                Control &amp; Surveillance
              </h3>
              <p className="text-gray-700">Monitoring phone/social media, isolating from friends/family, controlling finances</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-emotion-unhappy-line text-rose-600 text-xl"></i>
                Emotional Manipulation
              </h3>
              <p className="text-gray-700">Guilt-tripping, playing victim, threatening self-harm, silent treatment</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-shield-cross-line text-rose-600 text-xl"></i>
                Intimidation
              </h3>
              <p className="text-gray-700">Threatening violence, destroying property, aggressive behavior, making you feel unsafe</p>
            </div>
          </div>

          <p className="text-lg text-gray-800 leading-relaxed mb-6">
            Women experiencing psychological violence often feel they must <strong>perform strength</strong> to survive. They smile through the pain. They minimize the abuse. They convince themselves and others that they're fine.
          </p>

          <p className="text-lg text-gray-800 leading-relaxed">
            This performance is exhausting and isolating. It prevents women from seeking help because admitting vulnerability feels like admitting defeat. And society reinforces this by praising women who "stay strong" while offering little support to those who break down.
          </p>
        </div>

        {/* Image */}
        <div className="mb-12 rounded-2xl overflow-hidden">
          <img 
            src="https://readdy.ai/api/search-image?query=woman-sitting-alone-looking-sad-stressed-holding-head-in-hands-home-interior-natural-window-light-documentary-photography-realistic-candid-moment-emotional-distress-psychological-pain-photojournalism-style&width=1200&height=600&seq=structural2&orientation=landscape"
            alt="Woman experiencing psychological distress"
            className="w-full h-96 object-cover object-top"
          />
        </div>

        {/* Section 3: Hiding Trauma from Children */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">The Mother's Mask: Hiding Trauma to Protect Children</h2>
          
          <p className="text-lg text-gray-800 leading-relaxed mb-6">
            Perhaps the most heartbreaking performance is when mothers hide violence and trauma from their children by pretending to be happy.
          </p>

          <div className="bg-blue-50 border-l-4 border-blue-600 p-6 mb-8 rounded-r-lg">
            <p className="text-lg text-gray-800 italic">
              "After he hits me, I go to the bathroom and cry silently. I wash my face, fix my makeup, and come out smiling for my daughter. I don't want her to see me weak. I don't want her to know what's happening. But I'm teaching her that women should hide their pain and smile through abuse. What kind of lesson is that?"
            </p>
            <p className="text-sm text-gray-600 mt-3">— Maria, 32, France</p>
          </div>

          <p className="text-lg text-gray-800 leading-relaxed mb-6">
            Mothers in abusive situations face an impossible choice:
          </p>

          <div className="space-y-6 mb-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-emotion-sad-line text-rose-600 text-xl"></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Show the truth</h3>
                <p className="text-gray-700">Let children see the violence and trauma, which can cause lasting psychological damage and normalize abuse</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-emotion-happy-line text-rose-600 text-xl"></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Hide the truth</h3>
                <p className="text-gray-700">Pretend everything is fine, which teaches children to suppress emotions and accept dysfunction as normal</p>
              </div>
            </div>
          </div>

          <p className="text-lg text-gray-800 leading-relaxed mb-6">
            Research shows that children who witness domestic violence — even if they don't see physical abuse — experience:
          </p>

          <ul className="space-y-3 mb-8 ml-6">
            <li className="text-gray-800"><strong>Anxiety and depression</strong> at higher rates than peers</li>
            <li className="text-gray-800"><strong>Behavioral problems</strong> including aggression and withdrawal</li>
            <li className="text-gray-800"><strong>Difficulty forming healthy relationships</strong> in adulthood</li>
            <li className="text-gray-800"><strong>Increased risk of becoming victims or perpetrators</strong> of violence themselves</li>
            <li className="text-gray-800"><strong>Lower academic performance</strong> and concentration issues</li>
          </ul>

          <p className="text-lg text-gray-800 leading-relaxed">
            The mother's instinct to protect her children by hiding trauma is understandable. But it's a symptom of a system that fails to protect families from violence in the first place. Women shouldn't have to choose between exposing their children to trauma or teaching them to suppress reality.
          </p>
        </div>

        {/* Section 4: What is Structural Violence? */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Understanding Structural Violence</h2>
          
          <p className="text-lg text-gray-800 leading-relaxed mb-6">
            <strong>Structural violence</strong> refers to systematic ways in which social structures harm or disadvantage individuals. It's violence that's built into the system — so normalized that we often don't recognize it as violence at all.
          </p>

          <div className="bg-gradient-to-br from-rose-50 to-purple-50 p-8 rounded-2xl mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Examples of Structural Violence Against Women:</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-rose-600 text-xl mt-1"></i>
                <p className="text-gray-800"><strong>Gender pay gap:</strong> Women earning less for the same work, making it harder to leave abusive relationships</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-rose-600 text-xl mt-1"></i>
                <p className="text-gray-800"><strong>Victim-blaming culture:</strong> "What were you wearing?" "Why were you out alone?" "Why didn't you just leave?"</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-rose-600 text-xl mt-1"></i>
                <p className="text-gray-800"><strong>Inadequate legal protection:</strong> Laws that fail to prosecute abusers or protect survivors</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-rose-600 text-xl mt-1"></i>
                <p className="text-gray-800"><strong>Limited access to resources:</strong> Insufficient shelters, counseling, legal aid for survivors</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-rose-600 text-xl mt-1"></i>
                <p className="text-gray-800"><strong>Social expectations:</strong> Pressure to be "good wives," "strong mothers," "not too emotional"</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-rose-600 text-xl mt-1"></i>
                <p className="text-gray-800"><strong>Normalization of male violence:</strong> "Boys will be boys," "He just has a temper," "That's just how men are"</p>
              </div>
            </div>
          </div>

          <p className="text-lg text-gray-800 leading-relaxed">
            Structural violence is particularly insidious because it doesn't require individual perpetrators. The system itself perpetuates harm. And because it's normalized, victims often blame themselves rather than recognizing the systemic nature of their oppression.
          </p>
        </div>

        {/* Action Steps */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">What Can We Do?</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4 p-6 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl">
              <div className="w-8 h-8 flex items-center justify-center bg-teal-600 text-white rounded-full flex-shrink-0 mt-1">
                <span className="font-bold">1</span>
              </div>
              <div>
                <h3 className="font-bold text-lg mb-2 text-gray-900">Educate Ourselves</h3>
                <p className="text-gray-700">Learn about structural violence and how it operates in our communities. Understanding is the first step to change.</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-6 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl">
              <div className="w-8 h-8 flex items-center justify-center bg-teal-600 text-white rounded-full flex-shrink-0 mt-1">
                <span className="font-bold">2</span>
              </div>
              <div>
                <h3 className="font-bold text-lg mb-2 text-gray-900">Support Grassroots Organizations</h3>
                <p className="text-gray-700">Organizations led by affected communities are often best positioned to create meaningful change.</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-6 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl">
              <div className="w-8 h-8 flex items-center justify-center bg-teal-600 text-white rounded-full flex-shrink-0 mt-1">
                <span className="font-bold">3</span>
              </div>
              <div>
                <h3 className="font-bold text-lg mb-2 text-gray-900">Advocate for Policy Change</h3>
                <p className="text-gray-700">Contact representatives, sign petitions, and vote for policies that address systemic inequalities.</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-6 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl">
              <div className="w-8 h-8 flex items-center justify-center bg-teal-600 text-white rounded-full flex-shrink-0 mt-1">
                <span className="font-bold">4</span>
              </div>
              <div>
                <h3 className="font-bold text-lg mb-2 text-gray-900">Challenge Everyday Injustices</h3>
                <p className="text-gray-700">Speak up when you witness discrimination. Small actions can create ripples of change.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Resources Section */}
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-8 rounded-2xl border border-purple-200">

        </div>

        {/* Section 5: Breaking the Cycle */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Breaking the Cycle: What Needs to Change</h2>
          
          <p className="text-lg text-gray-800 leading-relaxed mb-8">
            Ending structural violence requires systemic change, not individual solutions. Here's what needs to happen:
          </p>

          <div className="space-y-6">
            <div className="bg-white border-2 border-purple-200 p-6 rounded-xl">
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">1</span>
                Education and Prevention
              </h3>
              <p className="text-gray-700 ml-13">
                Teach consent, healthy relationships, and gender equality from childhood. Challenge toxic masculinity and victim-blaming culture.
              </p>
            </div>

            <div className="bg-white border-2 border-purple-200 p-6 rounded-xl">
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">2</span>
                Legal Reform
              </h3>
              <p className="text-gray-700 ml-13">
                Strengthen laws against domestic violence, psychological abuse, and harassment. Ensure swift prosecution and adequate sentencing.
              </p>
            </div>

            <div className="bg-white border-2 border-purple-200 p-6 rounded-xl">
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">3</span>
                Support Services
              </h3>
              <p className="text-gray-700 ml-13">
                Fund shelters, counseling, legal aid, and economic support for survivors. Make these services accessible and well-publicized.
              </p>
            </div>

            <div className="bg-white border-2 border-purple-200 p-6 rounded-xl">
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">4</span>
                Economic Equality
              </h3>
              <p className="text-gray-700 ml-13">
                Close the gender pay gap. Provide affordable childcare. Ensure women have economic independence to leave abusive situations.
              </p>
            </div>

            <div className="bg-white border-2 border-purple-200 p-6 rounded-xl">
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">5</span>
                Cultural Shift
              </h3>
              <p className="text-gray-700 ml-13">
                Challenge the normalization of women's fear and performance of safety. Hold perpetrators accountable, not victims.
              </p>
            </div>
          </div>
        </div>

        {/* Conclusion */}
        <div className="mb-12 bg-gradient-to-br from-pink-50 to-purple-50 p-8 rounded-2xl">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">The Violence of "Normal"</h2>
          
          <p className="text-lg text-gray-800 leading-relaxed mb-6">
            When we accept that women must pretend to be on the phone to walk home safely, we're accepting violence as normal. When we expect women to act strong after psychological abuse, we're normalizing trauma. When mothers must hide violence from their children, we're perpetuating cycles of harm.
          </p>

          <p className="text-lg text-gray-800 leading-relaxed mb-6">
            <strong>This is not normal. This is not acceptable. This is structural violence.</strong>
          </p>

          <p className="text-lg text-gray-800 leading-relaxed">
            Real safety doesn't come from women performing strength or pretending to be unavailable. It comes from a society that refuses to tolerate violence against women in any form — physical, psychological, or structural.
          </p>
        </div>

        {/* Call to Action */}
        <div className="bg-gray-900 text-white p-8 rounded-2xl">
          <h2 className="text-3xl font-bold mb-6">Take Action</h2>
          <div className="space-y-4 mb-8">
            <div className="flex items-start gap-3">
              <i className="ri-arrow-right-line text-pink-400 text-xl mt-1"></i>
              <p className="text-lg">Set up your emergency safety contacts on our platform</p>
            </div>
            <div className="flex items-start gap-3">
              <i className="ri-arrow-right-line text-pink-400 text-xl mt-1"></i>
              <p className="text-lg">Sign our petition demanding stronger protections for women</p>
            </div>
            <div className="flex items-start gap-3">
              <i className="ri-arrow-right-line text-pink-400 text-xl mt-1"></i>
              <p className="text-lg">Share this article to raise awareness about structural violence</p>
            </div>
            <div className="flex items-start gap-3">
              <i className="ri-arrow-right-line text-pink-400 text-xl mt-1"></i>
              <p className="text-lg">Support organizations working to end violence against women</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => navigate('/safety')}
              className="bg-pink-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-pink-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              Safety Features
            </button>
            <button
              onClick={() => navigate('/demands')}
              className="bg-white text-gray-900 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              Sign Petition
            </button>
            <button
              onClick={() => navigate('/donate')}
              className="bg-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              Support Our Work
            </button>
          </div>
        </div>

      </article>

      <Footer />
    </div>
  );
}